/*
 * Module for controls for a snippet list
 */

define([
  "marionette",
  "underscore",
  "i18n/i18n",
  "messaging/messenger",
  "./snippetsControlsView",
  "modules/topicsSplit/snippet/snippetModel",
  "modules/shared/moveModal/moveModalModule",
], function (
  Marionette,
  _,
  i18Next,
  Messenger,
  SnippetsControlsView,
  SnippetModel,
  MoveModalModule,
) {
  return Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "snippetsControls";
      this.region = options.region;
      this.showButtons = options.showButtons;
    },
    show: function (group, rootSnippetGroup) {
      this.group = group;
      group.isAdmin = Messenger.reqres.request("user:is:admin");
      this.rootSnippetGroup = rootSnippetGroup;
      this.view = new SnippetsControlsView.snippetsControlsView({ model: group });
      if (group.isNew() && group.get("name").length === 0) {
        //auto-select group name textbox if the group is new
        this.view.once("show", () => this.view.enableTextbox("#snippetFolderText"));
      }

      //delete all the snippets that are selected
      this.view.on("delete:snippets", () => {
        //clear any old errors
        this.clearError();
        //get the checked models from the sortable list (with namespace)
        const snippetsToDelete = Messenger.reqres.request("snippetList:checked:models");
        if (snippetsToDelete.length > 0) {
          Messenger.confirmDialog(
            i18Next.t("snippets.dialog.typeToConfirmDelete"),
            {
              confirm: (comment) => {
                if (comment && comment.toUpperCase() === "DELETE") {
                  Messenger.vent.trigger("show:saving:display");
                  this._deleteSnippets(snippetsToDelete);
                }
              },
            },
            this,
            "snippets.remove",
            {
              doNotModifyPlaceholder: true,
              placeholder: "common.delete",
              textArea: false,
            },
          );
        } else {
          this.controlsError(i18Next.t("snippet.controls.emptyDelete"));
        }
      });

      //move all the snippets that are selected
      this.view.on("move:snippets", this._moveSnippets, this);

      //add sub folder to the current folder
      this.view.on("add:folder", this._addFolder, this);

      this.view.on("move:folder", this._moveFolder, this);

      //delete current folder
      this.view.on("delete:folder", this._deleteFolder, this);

      this.view.on("add:new:snippet", this._addNewSnippetToGroup, this);

      this.showRegion(this.view);
    },
    clearError: function () {
      this.view.clearError();
    },
    controlsError: function (errorMessage) {
      this.view.controlsError(errorMessage);
    },
    _addNewSnippetToGroup: function () {
      // save any pending changes first
      Messenger.save(function (success) {
        if (success) {
          const newSnippet = this.group.snippets().addNewSnippet(this.group.id);
          Messenger.vent.trigger("show:snippet:edit:page", newSnippet);
        }
      }, this);
    },
    _moveSnippets: function () {
      //clear any old errors
      this.clearError();
      const group = this.group;
      //get the checked models from the sortable list (with namespace)
      const snippetsToMove = Messenger.reqres.request("snippetList:checked:models");
      if (snippetsToMove.length > 0) {
        Messenger.save((success) => {
          //save before attempting the move
          if (success) {
            Messenger.vent.trigger(
              "show:modal",
              MoveModalModule,
              {},
              {
                modelsToMove: snippetsToMove,
                rootGroup: this.rootSnippetGroup,
                nameAttribute: "name",
                group: group,
              },
              {
                title: i18Next.t("snippets.move.title"),
                modalWidth: 400,
                trackInteraction: {
                  action: "Move Snippet to Folder",
                  object: "Snippet",
                },
              },
            );
          }
        });
      } else {
        this.controlsError(i18Next.t("snippet.controls.emptyMove"));
      }
    },
    _addFolder: function () {
      //check if we can close and save any open textboxes
      if (Messenger.reqres.request("save:textbox")) {
        const group = this.group;
        if (group.dirty) {
          Messenger.vent.trigger("show:saving:display");
          group
            .saveModelPromise()
            .then(() => {
              this._createNewFolderAndExpand(group);
            })
            .catch((errorMsg) => {
              console.error("Could not save group", errorMsg);
              Messenger.okDialog(errorMsg);
            })
            .finally(() => Messenger.vent.trigger("close:saving:display"));
        } else {
          this._createNewFolderAndExpand(group);
        }
      }
    },
    _createNewFolderAndExpand: function (group) {
      if (group.isValid()) {
        //need to get sub groups from the server first
        if (group.get("hasSubgroups")) {
          group.trigger("expand"); //expand group and load/show any subgroups
          //once the subGroups are synced, add a new group
          group.subGroups().onSynced(() => {
            const newGroup = group.addSubGroup();
            newGroup.trigger("select"); //select the new group for editing
          });
        } else {
          const newGroup = group.addSubGroup();
          group.trigger("expand");
          newGroup.trigger("select");
        }
      }
    },
    _moveFolder: function () {
      if (Messenger.reqres.request("save:textbox")) {
        Messenger.save((success) => {
          if (success) {
            const group = this.group;
            if (group.isValid()) {
              //remove the folder from the collection, and show the move modal
              group.parent = group.collection.parent; //save parent just in case we need to cancel move
              group.collection.remove(group);
              Messenger.vent.trigger(
                "show:modal",
                MoveModalModule,
                {},
                {
                  rootGroup: this.rootSnippetGroup,
                  nameAttribute: "name",
                  group: group,
                },
                {
                  title: i18Next.t("snippets.moveFolder.title"),
                  modalWidth: 400,
                  trackInteraction: {
                    action: "Move Snippet Folder",
                    object: "Snippet",
                  },
                },
              );
            }
          }
        });
      }
    },
    _deleteFolder: function () {
      const group = this.group;
      group.snippets().onSynced(() => {
        if (group.snippets().length === 0 && !group.get("hasSubgroups")) {
          Messenger.yesNoDialog("snippet.deleteFolder", {
            yes: () => {
              Messenger.vent.trigger("track:interaction", {
                action: "Delete Snippet Folder",
                id: group.id,
                idType: "Snippet Folder",
                object: "Snippet Folder",
              });
              const parentModel = group.collection.parent;
              Messenger.vent.trigger("cancel:textbox"); //cancel any open textboxes
              group.deleteModel();
              //switch to parent folder
              parentModel.trigger("select");
            },
          });
        } else {
          Messenger.okDialog("snippet.delete.nonEmptyFolder");
        }
      });
    },
    _deleteSnippets: function (snippetsToDelete) {
      if (!snippetsToDelete || !snippetsToDelete.length) {
        Messenger.vent.trigger("close:saving:display");
        return;
      }

      const snippetToDelete = snippetsToDelete.pop();
      snippetToDelete.deleteModel({
        success: () => {
          this._deleteSnippets(snippetsToDelete);
        },
        error: (model, response) => {
          Messenger.vent.trigger("close:saving:display");
          Messenger.showErrorFromResponse(response);
        },
      });
    },
  });
});
